import { Chip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { emailRegex } from "../../config/config";
import { soulManager } from "../../data/managers/SoulManager";
import {
  CreditApplication,
  CreditStatus,
} from "../../data/models/applications/CreditApplication";
import { useAuth } from "../../data/providers/AuthProvider";
import { useUserData } from "../../data/providers/UserDataProvider";
import { fmtMoney } from "../../utils/NumberTool";

type FormErrors = Partial<
  Record<
    | "applicantName"
    | "document"
    | "documentType"
    | "amount"
    | "termMonths"
    | "purpose"
    | "emailOptional",
    string
  >
>;

const onlyDigits = (v?: string) => (v || "").replace(/\D/g, "");

const nowIso = () => new Date().toISOString();

const StatusChip: React.FC<{ status: CreditStatus }> = ({ status }) => {
  const map: Record<
    CreditStatus,
    { label: string; color: "default" | "success" | "error" | "warning" }
  > = {
    PENDING: { label: "Pendiente", color: "warning" },
    APPROVED: { label: "Aprobada", color: "success" },
    REJECTED: { label: "Rechazada", color: "error" },
    FINISHED: { label: "Finalizada", color: "success" },
  };
  return (
    <Chip size="small" label={map[status].label} color={map[status].color} />
  );
};

const validateDocumentByType = (
  type: CreditApplication["documentType"],
  value: string
): string | undefined => {
  const digits = onlyDigits(value);
  if (type === "CC" || type === "TI") {
    if (digits.length < 6 || digits.length > 12) return "Documento inválido";
  } else if (type === "CE") {
    if (digits.length < 5 || digits.length > 12) return "Documento inválido";
  } else if (type === "NIT") {
    if (digits.length < 8 || digits.length > 15) return "NIT inválido";
  } else if (type === "PAS") {
    if (value.length < 5 || value.length > 15) return "Pasaporte inválido";
  }
  return undefined;
};

function validateForm(
  f: Partial<CreditApplication>,
  canEditIdentity: { name: boolean; doc: boolean; email: boolean }
): FormErrors {
  const errors: FormErrors = {};

  if (canEditIdentity.name) {
    if (!f.applicantName || f.applicantName.trim().length < 2) {
      errors.applicantName = "Nombre requerido (mín. 2 caracteres)";
    } else if (f.applicantName.length > 80) {
      errors.applicantName = "Máximo 80 caracteres";
    }
  }

  if (canEditIdentity.doc) {
    if (!f.documentType) {
      errors.documentType = "Selecciona un tipo";
    }
    if (!f.document) {
      errors.document = "Documento requerido";
    } else {
      const docErr = validateDocumentByType(
        (f.documentType as CreditApplication["documentType"]) || "CC",
        f.document
      );
      if (docErr) errors.document = docErr;
    }
  }

  if (
    f.amount == null ||
    isNaN(f.amount as number) ||
    (f.amount as number) <= 0
  ) {
    errors.amount = "Monto debe ser mayor a 0";
  }

  if (f.termMonths == null || isNaN(f.termMonths as number)) {
    errors.termMonths = "Plazo requerido";
  } else if ((f.termMonths as number) < 1 || (f.termMonths as number) > 84) {
    errors.termMonths = "Plazo entre 1 y 84 meses";
  }

  if (!f.purpose || f.purpose.trim().length < 3) {
    errors.purpose = "Propósito requerido (mín. 3 caracteres)";
  } else if (f.purpose.length > 120) {
    errors.purpose = "Máximo 120 caracteres";
  }

  if (
    canEditIdentity.email &&
    f.emailOptional &&
    !emailRegex.test(f.emailOptional)
  ) {
    errors.emailOptional = "Correo inválido";
  }

  return errors;
}

async function apiCreateOrUpdate({
  payload,
}: {
  payload: Partial<CreditApplication>;
}) {
  await soulManager.createSoulApplication(payload);
}

export function useAllApplications() {
  const userData = useUserData()!;
  const { user } = useAuth();
  const uid = user?.uid;

  const [items, setItems] = useState<CreditApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CreditApplication | null>(null);
  const [form, setForm] = useState<Partial<CreditApplication>>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const isEdit = Boolean(editing);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "info" });

  const profileHasName = Boolean(
    userData?.data?.fullName && userData.data?.fullName.trim().length >= 2
  );
  const profileHasDoc = Boolean(
    userData?.data?.document && userData?.data?.documentType
  );
  const profileHasEmail = Boolean(user?.email);
  const canEditIdentity = {
    name: !profileHasName,
    doc: !profileHasDoc,
    email: !profileHasEmail,
  };

  const loadApplications = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const rows = await soulManager.getAllApplicationsByAdmin();
      rows?.sort((a, b) => {
        if (a.status === "FINISHED" && b.status === "FINISHED") {
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        }
        if (!a.paymentDate) return 1;
        if (!b.paymentDate) return -1;
        return new Date(String(a.paymentDate)).getTime() - new Date(String(b.paymentDate)).getTime();
      });
      setItems(rows?.filter((r) => r.status !== "REJECTED") ?? []);
    } finally {
      setLoading(false);
    }
  };

  const createOrUpdateApplication = async (
    payload: Partial<CreditApplication>
  ) => {
    if (!uid) return;

    const base: Partial<CreditApplication> = {
      ...payload,
      applicantName: profileHasName
        ? (userData.data?.fullName as string)
        : payload.applicantName,
      document: profileHasDoc
        ? (userData.data?.document as string)
        : payload.document,
      documentType: profileHasDoc
        ? (userData.data?.documentType as any)
        : payload.documentType,
      emailOptional: profileHasEmail
        ? user?.email
        : payload.emailOptional || null,
      phoneOptional: user?.phoneNumber || null,
    };
    if (!payload.id) {
      base.status = "PENDING";
    }

    const out: Record<string, any> = {};
    Object.entries(base).forEach(([k, v]) => {
      if (v !== undefined) out[k] = v === "" ? null : v;
    });

    await apiCreateOrUpdate({ payload: base });
    await loadApplications();
  };

  const createInstallment = async (
    applicationId: string,
    installmentId: string,
    url: string
  ) => {
    const result = await soulManager.createInstallmentApplication({
      applicationId,
      installmentId,
      url,
    });
    if (!result) throw new Error("Error en createInstallment");
    await loadApplications();
  };

  useEffect(() => {
    if (uid) loadApplications();
  }, [uid]);

  const openCreate = () => {
    if (!profileHasName || !profileHasDoc) {
      alert(
        "Para crear una solicitud de crédito, primero debes completar tu información de perfil (nombre, documento). Dirígete a datos personales y actualiza tu información."
      );
      return;
    }
    setEditing(null);
    setForm({
      applicantName: profileHasName ? userData.data?.fullName : "",
      document: profileHasDoc ? (userData.data?.document as string) : "",
      documentType: profileHasDoc ? (userData.data?.documentType as any) : "CC",
      amount: 0,
      termMonths: 12,
      purpose: "",
      emailOptional: profileHasEmail ? user?.email || "" : "",
      phoneOptional: user?.phoneNumber || "",
    });
    setErrors({});
    setOpen(true);
  };

  const openEdit = (row: CreditApplication) => {
    setEditing(row);
    setForm({ ...row });
    setErrors({});
    setOpen(true);
  };

  const closeDialog = () => setOpen(false);

  const handleChange =
    (field: keyof CreditApplication) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setForm((prev) => ({
        ...prev,
        [field]:
          field === "amount" || field === "termMonths" ? Number(value) : value,
      }));
    };

  const enableConsentApplication = async (id: string) => {
    const result = await soulManager.enableConsentApplication(id);
    if (!result) throw new Error("Error en enableConsentApplication");
    await loadApplications();
  };

  const saveForm = async () => {
    const v = validateForm(form, canEditIdentity);
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    await createOrUpdateApplication({
      id: editing?.id,
      applicantName: form.applicantName!,
      document: form.document!,
      documentType: form.documentType as CreditApplication["documentType"],
      amount: Number(form.amount),
      termMonths: Number(form.termMonths),
      purpose: form.purpose!,
      emailOptional: (form.emailOptional || null) as string | null,
    });
    setOpen(false);
  };

  const enableConsent = async (id: string) => {
    try {
      setLoadingDefault(true);
      await enableConsentApplication(id);
      setSnackbar({
        open: true,
        message: "Solicitud actualizada correctamente ✅",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "No se pudo actualizar la solicitud. Intenta de nuevo.",
        severity: "error",
      });
    } finally {
      setLoadingDefault(false);
    }
  };

  const declineApplication = async (id: string) => {
    try {
      setLoadingDefault(true);
      const result = await soulManager.declineApplication(id);
      if (!result) throw new Error("Error en enableConsentApplication");
      await loadApplications();
      setSnackbar({
        open: true,
        message: "Solicitud actualizada correctamente ✅",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "No se pudo actualizar la solicitud. Intenta de nuevo.",
        severity: "error",
      });
    } finally {
      setLoadingDefault(false);
    }
  };

  const updateInstallment = async (
    applicationId: string,
    installmentId: string,
    interestAmount: number,
    capitalAmount: number,
    isPaymentInstallment: boolean,
    rejectReason: string | undefined,
    isRejected: boolean
  ) => {
    try {
      setLoadingDefault(true);
      const result = await soulManager.updateInstallmentApplication({
        applicationId,
        installmentId,
        interestAmount,
        capitalAmount,
        isPaymentInstallment,
        rejectReason,
        isRejected,
      });
      if (!result) throw new Error("Error en updateInstallmentApplication");
      setSnackbar({
        open: true,
        message: "Solicitud actualizada correctamente ✅",
        severity: "success",
      });
      await loadApplications();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "No se pudo actualizar la solicitud. Intenta de nuevo.",
        severity: "error",
      });
    } finally {
      setLoadingDefault(false);
    }
  };

  const uploadConsent = async (id: string, url: string) => {
    try {
      await soulManager.updateSoulApplicationByAdmin({
        id,
        consentPdfUploaded: url,
        isConsentAvailable: true,
      });
      await loadApplications();
    } catch (err) {
      console.error("Error en uploadConsent:", err);
    }
  };

  const uploadInstallment = async (
    applicationId: string,
    installmentId: string,
    url: string
  ) => {
    try {
      setLoadingDefault(true);
      await createInstallment(applicationId, installmentId, url);
      setSnackbar({
        open: true,
        message: "Pago enviado correctamente ✅",
        severity: "success",
      });
      setOpen(false);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "No se pudo enviar el pago. Intenta de nuevo.",
        severity: "error",
      });
    } finally {
      setLoadingDefault(false);
    }
  };

  const finalizeApplication = async (id: string, qualification: number) => {
    try {
      setLoadingDefault(true);
      const result = await soulManager.finalizeApplication(id, qualification);
      if (!result) throw new Error("Error en finalizeApplication");
      await loadApplications();
      setSnackbar({
        open: true,
        message: "Solicitud actualizada correctamente ✅",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "No se pudo actualizar la solicitud. Intenta de nuevo.",
        severity: "error",
      });
      return false;
    } finally {
      setLoadingDefault(false);
      return true;
    }
  };

  return {
    userData,
    loadApplications,
    loading,
    openCreate,
    user,
    items,
    fmtMoney,
    StatusChip,
    openEdit,
    isEdit,
    canEditIdentity,
    form,
    handleChange,
    errors,
    closeDialog,
    saveForm,
    open,
    loadingDefault,
    enableConsent,
    declineApplication,
    snackbar,
    setSnackbar,
    updateInstallment,
    finalizeApplication,
    uploadInstallment,
    uploadConsent,
  };
}
